import React, { Component } from 'react';
import { Dropdown, DropdownItemProps, DropdownProps, Grid, Header, Icon, Input, InputOnChangeData } from 'semantic-ui-react';
import { strings } from '../..';
import { Anagrafica, Column, DataTable, Moduli } from '../../core';
import { TipoIntervento } from '../../core-assistenza';
import { datetimeFormatter, getIconName } from '../../core/utility';
import { Chiamata, StatoFiltroChiamate } from '../models';
import { allStatiFiltroValues } from '../utility';
import { numeroDocumentoFormatterComputer, statoChiamataFormatter, tipoInterventoFormatter, dettagliFormatter, ubicazioneFormatter } from './Formatters';

type ElencoSegnalazioniComputerProps = {
    chiamate: ReadonlyArray<Chiamata>,
    page: number,
    pageSize: number,
    itemsCount: number,
    anagrafica: Anagrafica,
    moduli: Moduli,
    loadChiamate: (search: string, page: number, tipoIntervento?: TipoIntervento, stato?: number) => void,
    downloadFile: (idArchivio: string) => void
}

type ElencoSegnalazioniComputerState = {
    search: string,
    stato?: StatoFiltroChiamate,
    tipoIntervento?: TipoIntervento
}

export default class ElencoSegnalazioniComputer extends Component<ElencoSegnalazioniComputerProps, ElencoSegnalazioniComputerState> {
    constructor(props: ElencoSegnalazioniComputerProps) {
        super(props);

        this.state = {
            search: '',
            stato: StatoFiltroChiamate.Aperta,
        };
    }

    componentDidMount() {
        const { page } = this.props;

        this.onSearch(page);
    }

    render() {
        const { chiamate, itemsCount, page, pageSize, anagrafica } = this.props;
        const columns: Column<Chiamata>[] = [
            { key: 'numero', label: strings.chiamate.elenco.dataTable.numero, width: '1', textAlign: 'right', formatter: numeroDocumentoFormatterComputer },
            { key: 'data', label: strings.chiamate.elenco.dataTable.data, width: '1', formatter: datetimeFormatter },
            { key: 'numeroMacchina', label: strings.chiamate.elenco.dataTable.numMacchina, width: '1' },
            { key: 'codice', label: strings.chiamate.elenco.dataTable.matricola },
            { key: 'descrizione', label: strings.chiamate.elenco.dataTable.modello },
            { key: 'ubicazione', label: strings.chiamate.elenco.dataTable.ubicazione, formatter: ubicazioneFormatter },
            { key: 'tipoRichiesta', label: strings.chiamate.elenco.dataTable.tipoRichiesta },
            { key: 'tipoIntervento', label: strings.chiamate.elenco.dataTable.tipoIntervento, formatter: tipoInterventoFormatter },
            { key: 'stato', label: strings.chiamate.elenco.dataTable.stato, width: '1', formatter: statoChiamataFormatter },
            { key: 'userName', label: '', width: 1, textAlign: 'center', formatter: dettagliFormatter }];

        if (this.props.moduli.chiamateArchivi) {
            columns.push({ key: 'fileName', label: '', formatter: this.formatoDocumentoFormatter, width: 1, textAlign: 'center' });
        }

        const optionsTipiIntervento: DropdownItemProps[] = [
            { key: TipoIntervento.Hardware, value: TipoIntervento.Hardware, text: 'Intervento hardware' },
            { key: TipoIntervento.Software, value: TipoIntervento.Software, text: 'Intervento software' }
        ];

        const optionsStati: DropdownItemProps[] = allStatiFiltroValues.map(stato => ({
            key: stato.stato,
            value: stato.stato, 
            text: stato.text, 
            content: <><Icon name={stato.name} color={stato.color} /> {stato.text}</>
        }));

        return (
            <Grid stackable centered>
                <Grid.Row>
                    <Header as='h2' textAlign='center'>
                        <Icon name='warning sign' />
                        <Header.Content>
                            {strings.chiamate.elenco.titolo}
                            <Header.Subheader>{anagrafica.ragioneSociale}</Header.Subheader>
                        </Header.Content>
                    </Header>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column width='10'>
                        <Input
                            fluid
                            icon={<Icon onClick={this.onSearchClick} name="search" inverted circular link />}
                            placeholder="Ricerca..."
                            value={this.state.search}
                            onChange={this.onSearchChange}
                            onKeyPress={this.onKeyPress}
                        />
                    </Grid.Column>
                    <Grid.Column width='2'>
                        <Dropdown 
                            selection 
                            clearable 
                            fluid 
                            options={optionsStati}                            
                            defaultValue={this.state.stato} 
                            onChange={this.onStatoChange} 
                        />
                    </Grid.Column>
                    <Grid.Column width='2'>
                        <Dropdown selection clearable fluid options={optionsTipiIntervento} onChange={this.onTipoInterventoChange} />
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column widescreen='14' computer='15' tablet='15' mobile='16'>
                        <DataTable<Chiamata>
                            dataSource={chiamate} 
                            primaryKey='idChiamata' 
                            columns={columns} 
                            page={page}                            
                            pageSize={pageSize}
                            itemsCount={itemsCount}
                            onPageChanged={this.onPageChanged} 
                        />
                    </Grid.Column>
                </Grid.Row>
            </Grid>
        );
    }

    onSearchChange = (_: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
        this.setState({ search: data.value });
    }

    onPageChanged = (page: number) => {
        this.onSearch(page);
    }

    onKeyPress = (event: any) => {
        if (event.key === 'Enter') {
            this.onSearch(this.props.page);
        }
    }

    onStatoChange = (_: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) => {
        const stato = data.value === '' ? undefined : data.value as StatoFiltroChiamate;
        this.setState({ stato: stato }, () => this.onSearch(this.props.page));
    }

    onTipoInterventoChange = (_: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) => {
        this.setState({ tipoIntervento: data.value as TipoIntervento }, () => this.onSearch(this.props.page));
    }

    onSearchClick = () => {
        this.onSearch(this.props.page);
    }

    onSearch = (page: number) => {
        const { search, tipoIntervento, stato } = this.state;
        this.props.loadChiamate(search, page, tipoIntervento, stato);
    }

    formatoDocumentoFormatter = (value: any, item: Chiamata) => {
        if (item.idArchivio === null) {
            return <></>;
        }

        const icon = getIconName(String(value));
        return (
            <Icon size='large' link name={icon.name} color={icon.color} onClick={this.onFileClick} id={item.idArchivio} />
        );
    }

    onFileClick = (_: any, data: any) => {
        this.props.downloadFile(String(data.id));
    }
}
